import React, { useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import axios from 'axios';
import notify from '../../../utils/notify';
import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import ActionButton from '../../../Components/Common/ActionButton';
import {
  FormGrid,
  TextField,
  focusFirstError,
  hasErrors,
  notBelow,
  validateForm,
} from '../../../Components/Form';

const PORT_SCHEMA = {
  dwtMax: { label: 'DWT max', rules: [notBelow('dwtMin', 'DWT min')] },
  loaMax: { label: 'LOA max', rules: [notBelow('loaMin', 'LOA min')] },
  draftMax: { label: 'Draft max', rules: [notBelow('draftMin', 'draft min')] },
};
const PORT_ORDER = ['dwtMax', 'loaMax', 'draftMax'];

const EMPTY_EDIT_PORT_FORM = {
  dwtMin: '',
  dwtMax: '',
  loaMin: '',
  loaMax: '',
  draftMin: '',
  draftMax: '',
};

const toNumberOrNull = value =>
  value === '' || value === null || value === undefined ? null : Number(value);

const toFormNumber = value => value ?? '';

/**
 * Modal untuk mengubah nilai DWT/LOA/Draft satu jetty Port Restriction
 * procurement. Location & nama jetty readonly. Save memakai
 * PUT /procurement-port-restriction/{id} — hanya data procurement ini yang
 * berubah, master jetty tidak tersentuh.
 */
const EditPortModal = ({ isOpen, toggle, item, onSaved }) => {
  const [formData, setFormData] = useState(EMPTY_EDIT_PORT_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !item?.id) {
      setFormData(EMPTY_EDIT_PORT_FORM);
      setErrors({});
      return;
    }
    setFormData({
      dwtMin: toFormNumber(item.dwtMin),
      dwtMax: toFormNumber(item.dwtMax),
      loaMin: toFormNumber(item.loaMin),
      loaMax: toFormNumber(item.loaMax),
      draftMin: toFormNumber(item.draftMin),
      draftMax: toFormNumber(item.draftMax),
    });
  }, [isOpen, item]);

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validatePort = () => {
    const nextErrors = validateForm(formData, PORT_SCHEMA);
    setErrors(nextErrors);
    if (hasErrors(nextErrors)) focusFirstError(nextErrors, PORT_ORDER);
    return !hasErrors(nextErrors);
  };

  const handleClose = () => {
    if (saving) {
      return;
    }
    setFormData(EMPTY_EDIT_PORT_FORM);
    setErrors({});
    toggle();
  };

  const handleSave = async () => {
    if (!item?.id) {
      notify.error('Port data is missing. Please reopen the edit dialog.');
      return;
    }
    if (!validatePort()) {
      return;
    }

    const payload = {
      draftMin: toNumberOrNull(formData.draftMin),
      draftMax: toNumberOrNull(formData.draftMax),
      loaMin: toNumberOrNull(formData.loaMin),
      loaMax: toNumberOrNull(formData.loaMax),
      dwtMin: toNumberOrNull(formData.dwtMin),
      dwtMax: toNumberOrNull(formData.dwtMax),
    };

    const token = getAuthToken();
    setSaving(true);
    try {
      await axios.put(
        `${api.API_URL}/procurement-port-restriction/${item.id}`,
        payload,
        {
          withXSRFToken: true,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      notify.success('Port restriction updated.');
      setErrors({});
      toggle();
      if (onSaved) {
        onSaved();
      }
    } catch (error) {
      notify.apiError(error, 'update the port');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size='lg'>
      <ModalHeader toggle={handleClose} className='fw-semibold'>
        Edit Port
      </ModalHeader>
      <ModalBody>
        <p className='text-muted fs-13'>
          Changes apply to this procurement only.
        </p>
        <FormGrid columns={2}>
          <TextField
            label='Location'
            name='locationName'
            mode='readonly'
            value={item?.locationName || '-'}
          />
          <TextField
            label='Jenis Sarana Tambat'
            name='name'
            mode='readonly'
            value={item?.name || '-'}
          />
        </FormGrid>
        <p className='hy-subhead'>Vessel limits (leave empty for no limit)</p>
        <FormGrid columns={2}>
          <TextField
            label='DWT min'
            name='dwtMin'
            placeholder='No limit'
            type='number'
            suffix='Ton'
            value={formData.dwtMin}
            onChange={e => handleFieldChange('dwtMin', e.target.value)}
          />
          <TextField
            label='DWT max'
            name='dwtMax'
            placeholder='No limit'
            type='number'
            suffix='Ton'
            value={formData.dwtMax}
            error={errors.dwtMax}
            onChange={e => handleFieldChange('dwtMax', e.target.value)}
          />
          <TextField
            label='LOA min'
            name='loaMin'
            placeholder='No limit'
            type='number'
            suffix='m'
            value={formData.loaMin}
            onChange={e => handleFieldChange('loaMin', e.target.value)}
          />
          <TextField
            label='LOA max'
            name='loaMax'
            placeholder='No limit'
            type='number'
            suffix='m'
            value={formData.loaMax}
            error={errors.loaMax}
            onChange={e => handleFieldChange('loaMax', e.target.value)}
          />
          <TextField
            label='Draft min'
            name='draftMin'
            placeholder='No limit'
            type='number'
            suffix='m'
            value={formData.draftMin}
            onChange={e => handleFieldChange('draftMin', e.target.value)}
          />
          <TextField
            label='Draft max'
            name='draftMax'
            placeholder='No limit'
            type='number'
            suffix='m'
            value={formData.draftMax}
            error={errors.draftMax}
            onChange={e => handleFieldChange('draftMax', e.target.value)}
          />
        </FormGrid>
      </ModalBody>

      <ModalFooter>
        <ActionButton
          variant='secondary'
          size='sm'
          icon='ri-close-line'
          disabled={saving}
          onClick={handleClose}
        >
          Close
        </ActionButton>
        <ActionButton
          variant='primary'
          size='sm'
          icon='ri-save-line'
          loading={saving}
          onClick={handleSave}
        >
          Save
        </ActionButton>
      </ModalFooter>
    </Modal>
  );
};

export default EditPortModal;
